import { useState } from "react";
import dynamic from "next/dynamic";
import Image from "next/image";

import { X } from "lucide-react";
import toast from "react-hot-toast";

import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { VisuallyHidden } from "@/components/ui/visually-hidden";
import { cn } from "@/lib/utils";
import { withIkVideoQuality } from "@/lib/utils/marketing";

const ReactPlayer = dynamic(() => import("react-player"), { ssr: false });

export function BaseVideoModal({ open, onClose, url }: { open: boolean; onClose: () => void; url: string }) {
  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent
        showCloseButton={false}
        className="p-4 w-[calc(100vw-1rem)] lg:w-[calc(50vw-2rem)] max-w-[90vw] bg-black rounded-lg border-none"
      >
        <VisuallyHidden>
          <DialogTitle>Video Player</DialogTitle>
        </VisuallyHidden>
        <div className="flex justify-end mb-2">
          <button onClick={onClose} className="text-white" aria-label="Close video player">
            <X className="h-5 w-5" />
          </button>
        </div>
        {open && (
          <ReactPlayer
            src={withIkVideoQuality(url)}
            controls
            playing
            // iOS blocks programmatic unmuted autoplay (NotAllowedError); start muted
            // and inline so playback is allowed. `controls` lets the user unmute.
            muted
            playsInline
            width="100%"
            height="450px"
            onError={() => {
              toast.error("Error playing video, please try again later");
              onClose();
            }}
          />
        )}
      </DialogContent>
    </Dialog>
  );
}

const VideoModalWithThumbnail = ({
  className,
  videoUrl,
  thumbnailUrl,
}: {
  className?: string;
  videoUrl: string;
  thumbnailUrl: string;
}) => {
  const [videoOpen, setVideoOpen] = useState(false);
  return (
    <div className={cn("relative", className)}>
      <Image
        src={thumbnailUrl}
        alt="video-thumbnail"
        sizes="(max-width: 768px) 90vw, 400px"
        width={400}
        height={550}
        priority={false}
        className="w-full h-full object-cover rounded-[20px]"
      />
      <button
        type="button"
        aria-label="Play video"
        onClick={() => setVideoOpen(true)}
        className="absolute inset-0 flex items-center justify-center"
      >
        <div className="w-20 h-20 bg-white/30 backdrop-blur-xs rounded-full flex items-center justify-center hover:scale-[1.05] transition-all">
          {/* play icon */}
          <svg
            width={22}
            className="translate-x-0.5"
            viewBox="0 0 26 29"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M23.8456 11.5376C26.3179 12.965 26.3179 16.5334 23.8456 17.9608L6.02621 28.2489C3.5539 29.6762 0.463523 27.892 0.463523 25.0372L0.463524 4.46118C0.463524 1.60641 3.55391 -0.177824 6.02622 1.24956L23.8456 11.5376Z"
              fill="white"
            />
          </svg>
        </div>
      </button>
      <BaseVideoModal open={videoOpen} onClose={() => setVideoOpen(false)} url={videoUrl} />
    </div>
  );
};

export default VideoModalWithThumbnail;
